import { Toaster } from '@parsleyai/ui'
import type React from 'react'
import { lazy, Suspense } from 'react'
import { Navigate, Outlet, Route, BrowserRouter as Router, Routes } from 'react-router-dom'
import BottomTabBar from '@/components/BottomTabBar'
import LoadingScreen from '@/components/LoadingScreen'
import ProtectedRoute from '@/components/ProtectedRoute'
import PushDeepLinkListener from '@/components/PushDeepLinkListener'
import RouteErrorBoundary from '@/components/RouteErrorBoundary'
import ScrollToTop from '@/components/ScrollToTop'
import { CREATE_RULES_PATH } from '@/pages/foodProfiles/create/createPaths'
import { StoreProvider, useAuth } from '@/store'

const AddRecipePage = lazy(() => import('@/pages/AddRecipePage'))
const HomePage = lazy(() => import('@/pages/HomePage'))
const LoginPage = lazy(() => import('@/pages/LoginPage'))
const SignupPage = lazy(() => import('@/pages/SignupPage'))
const NoInvitePage = lazy(() => import('@/pages/NoInvitePage'))
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'))
const MealPlansPage = lazy(() => import('@/pages/mealPlans/MealPlansPage'))
const MealPlanDetailPage = lazy(() => import('@/pages/mealPlans/MealPlanDetailPage'))
const CookingHistoryPage = lazy(() => import('@/pages/mealPlans/CookingHistoryPage'))
const BuildPlanPage = lazy(() => import('@/pages/mealPlans/buildPlan/BuildPlanPage'))
const FinalizeDraftPage = lazy(() => import('@/pages/mealPlans/finalizeDraft/FinalizeDraftPage'))
const MealViewRouter = lazy(() => import('@/experiments/meal-v2/MealViewRouter'))
const CookingModePage = lazy(() => import('@/pages/cooking/CookingModePage'))
const FoodProfilesModal = lazy(() => import('@/pages/foodProfiles/FoodProfilesModal').then((m) => ({ default: m.FoodProfilesModal })))
const FoodProfilesListView = lazy(() =>
  import('@/pages/foodProfiles/FoodProfilesListView').then((m) => ({ default: m.FoodProfilesListView }))
)
const CreateProfileFlow = lazy(() =>
  import('@/pages/foodProfiles/create/CreateProfileFlow').then((m) => ({ default: m.CreateProfileFlow }))
)
const ProfileHubPage = lazy(() => import('@/pages/foodProfiles/hub/ProfileHubPage').then((m) => ({ default: m.ProfileHubPage })))
const HubRedirect = lazy(() => import('@/pages/foodProfiles/hub/HubRedirect').then((m) => ({ default: m.HubRedirect })))
const CreateProfileNameScreen = lazy(() =>
  import('@/pages/foodProfiles/create/CreateProfileNameScreen').then((m) => ({ default: m.CreateProfileNameScreen }))
)
const CreationRulesStep = lazy(() =>
  import('@/pages/foodProfiles/create/CreationRulesStep').then((m) => ({ default: m.CreationRulesStep }))
)
const EatingStylePage = lazy(() => import('@/pages/EatingStyle/EatingStylePage').then((m) => ({ default: m.EatingStylePage })))
const EatingPreferencesFlow = lazy(() =>
  import('@/pages/eatingPreferences/EatingPreferencesFlow').then((m) => ({ default: m.EatingPreferencesFlow }))
)
const EatingPreferencesHub = lazy(() =>
  import('@/pages/eatingPreferences/EatingPreferencesHub').then((m) => ({ default: m.EatingPreferencesHub }))
)
const PlateShapeEditor = lazy(() =>
  import('@/pages/eatingPreferences/editors/PlateShapeEditor').then((m) => ({ default: m.PlateShapeEditor }))
)
const CuisinesEditor = lazy(() => import('@/pages/eatingPreferences/editors/CuisinesEditor').then((m) => ({ default: m.CuisinesEditor })))
const EffortEditor = lazy(() => import('@/pages/eatingPreferences/editors/EffortEditor').then((m) => ({ default: m.EffortEditor })))
const FrequencyLimitsEditor = lazy(() =>
  import('@/pages/eatingPreferences/editors/FrequencyLimitsEditor').then((m) => ({ default: m.FrequencyLimitsEditor }))
)
const NutritionEditor = lazy(() =>
  import('@/pages/eatingPreferences/editors/NutritionEditor').then((m) => ({ default: m.NutritionEditor }))
)
const CookiePolicyPage = lazy(() => import('@/pages/CookiePolicyPage'))
const PrivacyPolicyPage = lazy(() => import('@/pages/PrivacyPolicyPage'))
const ProfilePage = lazy(() => import('@/pages/ProfilePage'))
const RecipesPage = lazy(() => import('@/pages/recipes/RecipesPage'))
const SettingsIndex = lazy(() => import('@/pages/settings/SettingsIndex'))
const SettingsLayout = lazy(() => import('@/pages/settings/SettingsLayout'))
const NotificationsSettings = lazy(() => import('@/pages/settings/NotificationsSettings'))
const PreferencesSettings = lazy(() => import('@/pages/settings/PreferencesSettings'))
const PrivacySettings = lazy(() => import('@/pages/settings/PrivacySettings'))
const HelpSettings = lazy(() => import('@/pages/settings/HelpSettings'))
const SubscriptionSettings = lazy(() => import('@/pages/settings/SubscriptionSettings'))
const SubscribeRoute = lazy(() => import('@/pages/subscribe/SubscribeRoute'))
const SubscribeReturnRoute = lazy(() => import('@/pages/subscribe/SubscribeReturnRoute'))
const ResetPasswordPage = lazy(() => import('@/pages/ResetPasswordPage'))
const TermsOfServicePage = lazy(() => import('@/pages/TermsOfServicePage'))
const WhatsNewPage = lazy(() => import('@/pages/WhatsNewPage'))
const AccountDeletedPage = lazy(() => import('@/pages/AccountDeletedPage'))

function AppRoutes() {
  const { loading, initialized } = useAuth()

  if (!initialized || loading) {
    return <LoadingScreen />
  }

  return (
    <>
      <RouteErrorBoundary>
        <Suspense fallback={<LoadingScreen />}>
          <Routes>
            <Route path="/" element={<Navigate to="/home" replace />} />
            <Route
              path="/home"
              element={
                <ProtectedRoute>
                  <HomePage />
                </ProtectedRoute>
              }
            />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/no-invite" element={<NoInvitePage />} />
            <Route
              path="/add-recipe"
              element={
                <ProtectedRoute>
                  <AddRecipePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/recipes"
              element={
                <ProtectedRoute>
                  <RecipesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/meal-plans"
              element={
                <ProtectedRoute>
                  <MealPlansPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/meal-plans/new"
              element={
                <ProtectedRoute>
                  <BuildPlanPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/meal-plans/history"
              element={
                <ProtectedRoute>
                  <CookingHistoryPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/meal-plans/:id/edit"
              element={
                <ProtectedRoute>
                  <FinalizeDraftPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/meal-plans/:id"
              element={
                <ProtectedRoute>
                  <MealPlanDetailPage />
                </ProtectedRoute>
              }
            />
            {/* Cook-addressed meal detail + cooking (#662): a dinner is a cook instance
                of a meal. cookId is globally unique, so it identifies the cooking on its
                own; the owning plan is resolved from the subscribed plans. */}
            <Route
              path="/cook/:cookId/cooking"
              element={
                <ProtectedRoute>
                  <CookingModePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/cook/:cookId"
              element={
                <ProtectedRoute>
                  <MealViewRouter />
                </ProtectedRoute>
              }
            />
            {/* Library-addressed meal detail (recipe only, no cook instance). Dormant
                until a meal-library surface exists — no links point here yet. */}
            <Route
              path="/meal/:mealId/cooking"
              element={
                <ProtectedRoute>
                  <CookingModePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/meal/:mealId"
              element={
                <ProtectedRoute>
                  <MealViewRouter />
                </ProtectedRoute>
              }
            />
            <Route
              path="/eating-style"
              element={
                <ProtectedRoute>
                  <Outlet />
                </ProtectedRoute>
              }>
              {/* Root: eating style summary page */}
              <Route index element={<EatingStylePage />} />
              {/* Food profiles modal — shell owns the overlay; list + create + edit all render inside it */}
              <Route path="food-profiles" element={<FoodProfilesModal />}>
                <Route index element={<FoodProfilesListView />} />
                {/* Creation is two named steps, each carrying its own chrome. There is no review screen:
                    the last step's `Save profile` IS the commit, and it lands on the new profile's hub.
                    `step/1`, `step/2`, `review` and `limits` redirect, since they're in history and in bookmarks.

                    The rule editor is a SHEET, not a route — it layers over whichever screen opened it so
                    that screen stays visible behind the scrim. The old `step/1/rule/*` takeovers replaced
                    the step underneath, which is why those are gone rather than redirected. */}
                <Route path="create" element={<CreateProfileFlow />}>
                  <Route index element={<CreateProfileNameScreen />} />
                  <Route path="rules" element={<CreationRulesStep />} />
                  <Route path="step/1" element={<Navigate to={CREATE_RULES_PATH} replace />} />
                  <Route path="step/2" element={<Navigate to={CREATE_RULES_PATH} replace />} />
                  <Route path="review" element={<Navigate to={CREATE_RULES_PATH} replace />} />
                  <Route path="limits" element={<Navigate to={CREATE_RULES_PATH} replace />} />
                </Route>
                {/* One edit screen that writes as it goes, so there's no flow to be inside. The other
                    `:profileId/*` paths only survive as redirects, because they're in history and in bookmarks. */}
                <Route path=":profileId" element={<ProfileHubPage />} />
                <Route path=":profileId/rules" element={<HubRedirect />} />
                <Route path=":profileId/limits" element={<HubRedirect />} />
                <Route path=":profileId/edit/*" element={<HubRedirect />} />
              </Route>
              {/* Eating preferences wizard */}
              <Route path="eating-preferences" element={<EatingPreferencesFlow />}>
                <Route index element={<EatingPreferencesHub />} />
                <Route path="plate-shape" element={<PlateShapeEditor />} />
                <Route path="cuisines" element={<CuisinesEditor />} />
                <Route path="effort" element={<EffortEditor />} />
                <Route path="frequency-limits" element={<FrequencyLimitsEditor />} />
                <Route path="nutrition" element={<NutritionEditor />} />
              </Route>
            </Route>
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <SettingsLayout />
                </ProtectedRoute>
              }>
              <Route index element={<SettingsIndex />} />
              <Route path="profile" element={<ProfilePage />} />
              <Route path="subscription" element={<SubscriptionSettings />} />
              <Route path="notifications" element={<NotificationsSettings />} />
              <Route path="preferences" element={<PreferencesSettings />} />
              <Route path="privacy" element={<PrivacySettings />} />
              <Route path="help" element={<HelpSettings />} />
            </Route>
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsOfServicePage />} />
            <Route path="/cookies" element={<CookiePolicyPage />} />
            <Route
              path="/subscribe"
              element={
                <ProtectedRoute>
                  <SubscribeRoute />
                </ProtectedRoute>
              }
            />
            {/* Where Stripe Checkout returns to. Protected: it completes a conversion for the
                signed-in caller, and the session id in the query is not an authentication. */}
            <Route
              path="/subscribe/return"
              element={
                <ProtectedRoute>
                  <SubscribeReturnRoute />
                </ProtectedRoute>
              }
            />
            <Route path="/whats-new" element={<WhatsNewPage />} />
            <Route path="/account-deleted" element={<AccountDeletedPage />} />
            <Route path="/profile" element={<Navigate to="/settings/profile" replace />} />
            <Route path="/account" element={<Navigate to="/settings/profile" replace />} />
            {/* Unmatched URLs render the 404 in place rather than redirecting to /home — the bad
                address stays in the bar, so it's visible, shareable, and Back returns where it should. */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </RouteErrorBoundary>
      <BottomTabBar />
    </>
  )
}

const AppInner: React.FC = () => (
  <StoreProvider>
    <ScrollToTop />
    <PushDeepLinkListener />
    <AppRoutes />
    <Toaster
      position="bottom-right"
      mobilePosition="bottom-center"
      mobileOffset={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 5.5rem)' }}
    />
  </StoreProvider>
)

const App: React.FC = () => (
  <Router>
    <AppInner />
  </Router>
)

export default App
